import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Input } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { listWorkerReview, resolveWorkerReviewBulk, resolveWorkerReviewRow } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import WorkersNav from "./workers/WorkersNav";
import { issueInfo, REVIEW_ORDER } from "./workers/workerIssues";
import { useWorkerSetup, WorkerCategory } from "./workers/useWorkerSetup";
import "./workers/karkhana-workers.scss";
import PageHeader from "./ui/PageHeader";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const LEAVE_MS = 200;

const numberOf = (code?: string) => {
  const m = String(code || "").match(/(-?\d+)$/);
  return m ? m[1] : "";
};

const defaultCategory = (row: any, categories: WorkerCategory[]) => {
  if (row.categoryId) return row.categoryId;
  const byPrefix = categories.find((c) => c.prefix === (row.suggestedPrefix || row.prefix));
  return byPrefix?.id || "";
};

type RowProps = {
  row: any;
  categories: WorkerCategory[];
  selected: boolean;
  leaving: boolean;
  disabled: boolean;
  onSelect: (checked: boolean) => void;
  onResolve: (row: any, body: Record<string, unknown>) => Promise<void>;
};

const ReviewRow = ({ row, categories, selected, leaving, disabled, onSelect, onResolve }: RowProps) => {
  const [categoryId, setCategoryId] = useState(() => defaultCategory(row, categories));
  const [name, setName] = useState("");
  const [note, setNote] = useState(row.resolutionNote || "");
  const [copyAddress, setCopyAddress] = useState(false);
  const blankName = (row.issues || []).includes("BLANK_NAME");
  const category = categories.find((c) => c.id === categoryId);
  const num = numberOf(row.proposedCode || row.rawCode);
  // When the sheet's code is taken, the server gives a fresh code instead of reusing the number.
  const codeTaken = (row.issues || []).some((i: string) => i === "CODE_EXISTS" || i === "DUPLICATE_CODE_IN_FILE");
  const createLabel = !category
    ? "Add as new"
    : num && !codeTaken
    ? `Add as ${category.prefix}_${num}`
    : `Add with new ${category.prefix} code`;
  const firstIssue = (row.issues || []).find((i: string) => REVIEW_ORDER.includes(i)) || (row.issues || [])[0];

  return (
    <div className={`kw-review-row${leaving ? " is-leaving" : ""}`}>
      <Input
        type="checkbox"
        checked={selected}
        disabled={disabled}
        onChange={(e) => onSelect(e.target.checked)}
        aria-label={`Select ${row.rawCode || "row"} ${row.rawName || ""}`}
        style={{ position: "static", margin: "4px 0 0" }}
      />
      <div className="kw-review-row__side">
        <h4>Sheet says · row {row.rowNo}</h4>
        <div className="kw-review-row__name">
          <span className="kw-code">{row.rawCode || "—"}</span> {row.rawName || <em className="kw-muted">no name</em>}
        </div>
        {row.rawAddress ? <div className="kw-muted">{row.rawAddress}</div> : null}
        <div className="kw-chips" style={{ marginTop: 6 }}>
          {(row.issues || []).map((i: string) => (
            <span key={i} className="kw-chip is-review">
              {issueInfo(i).label}
            </span>
          ))}
          {(row.warnings || []).map((w: string) => (
            <span key={w} className="kw-chip is-info">
              {issueInfo(w).label}
            </span>
          ))}
        </div>
      </div>
      <div className="kw-review-row__side kw-review-row__side--hkp">
        <h4>In HKP</h4>
        {(row.matchedWorkers || []).length ? (
          row.matchedWorkers.map((w: any) => (
            <div key={w.id} style={{ marginBottom: 6 }}>
              <div className="kw-review-row__name">
                <span className="kw-code">{w.workerCode}</span> {w.name}
              </div>
              {w.address ? <div className="kw-muted">{w.address}</div> : null}
              <div className="kw-toolbar" style={{ margin: "6px 0 0" }}>
                <Button size="sm" className="ResetBtn" disabled={disabled} onClick={() => onResolve(row, {
                  action: "LINK",
                  workerId: w.id,
                  copyFields: copyAddress ? ["address"] : [],
                  note: note.trim() || undefined,
                })}>
                  Same person
                </Button>
                {row.rawAddress && row.rawAddress !== w.address ? (
                  <label className="kw-muted" style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: 0 }}>
                    <input type="checkbox" checked={copyAddress} onChange={(e) => setCopyAddress(e.target.checked)} />
                    also use sheet address
                  </label>
                ) : null}
              </div>
            </div>
          ))
        ) : (
          <div className="kw-muted">No worker with this code</div>
        )}
      </div>
      {firstIssue ? <p className="kw-review-row__help">{issueInfo(firstIssue).help}</p> : null}
      <div className="kw-review-row__actions">
        {blankName ? (
          <Input
            placeholder="Name, once known"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Worker name"
          />
        ) : null}
        <Input
          type="select"
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          aria-label="Category"
        >
          <option value="">Choose category</option>
          {categories
            .filter((c) => c.active)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.prefix} · {c.name}
              </option>
            ))}
        </Input>
        <Button
          size="sm"
          className="ResetBtn"
          disabled={disabled || !categoryId || (blankName && !name.trim())}
          onClick={() =>
            onResolve(row, {
              action: "CREATE",
              categoryId,
              name: name.trim() || undefined,
              note: note.trim() || undefined,
            })
          }
        >
          {createLabel}
        </Button>
        <Button size="sm" outline disabled={disabled} onClick={() => onResolve(row, { action: "NOT_A_WORKER", note: note.trim() || undefined })}>
          Not a worker
        </Button>
        <Button size="sm" outline disabled={disabled} onClick={() => onResolve(row, { action: "SKIP", note: note.trim() || undefined })}>
          Skip
        </Button>
        <Input
          placeholder="Note (e.g. ask supervisor)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Note"
        />
        <Button
          size="sm"
          outline
          disabled={disabled || !note.trim() || note.trim() === (row.resolutionNote || "")}
          onClick={() => onResolve(row, { action: "NOTE", note: note.trim() })}
        >
          Keep for later
        </Button>
      </div>
    </div>
  );
};

const KarkhanaWorkerReviewPage = () => {
  const history = useHistory();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const { categories, loaded: setupLoaded, reload: reloadSetup } = useWorkerSetup();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // The reason filter is in the address (?reason=…, replaced).
  const [filter, setFilter] = useAddressParam("reason");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [leaving, setLeaving] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [bulkCategory, setBulkCategory] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all: any[] = [];
      for (let page = 0; page < 25; page++) {
        const res: any = await listWorkerReview({ offset: page, limit: 200 });
        const docs = res?.success ? res.data?.docs || [] : [];
        all.push(...docs);
        if (docs.length < 200 || all.length >= Number(res?.data?.length || 0)) break;
      }
      setRows(all);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load();
  }, [role, history, load]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    rows.forEach((r) => (r.issues || []).forEach((i: string) => map.set(i, (map.get(i) || 0) + 1)));
    const rank = (code: string) => (REVIEW_ORDER.indexOf(code) < 0 ? 99 : REVIEW_ORDER.indexOf(code));
    return Array.from(map.entries()).sort((a, b) => rank(a[0]) - rank(b[0]));
  }, [rows]);

  // When the last row of the chosen reason is resolved, go back to showing everything.
  useEffect(() => {
    // Not while loading: a reason from the address must survive until the rows are in.
    if (!loading && filter && !counts.some(([code]) => code === filter)) setFilter("");
  }, [counts, filter, setFilter, loading]);

  const visible = filter ? rows.filter((r) => (r.issues || []).includes(filter)) : rows;

  /** Fade the row out, then drop it from the list. Rows kept with a note stay. */
  const removeRows = (ids: string[]) => {
    setLeaving((prev) => new Set([...Array.from(prev), ...ids]));
    window.setTimeout(() => {
      setRows((prev) => prev.filter((r) => !ids.includes(r.id)));
      setLeaving((prev) => new Set(Array.from(prev).filter((id) => !ids.includes(id))));
      setSelected((prev) => new Set(Array.from(prev).filter((id) => !ids.includes(id))));
    }, LEAVE_MS);
  };

  const resolveOne = async (row: any, body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const res: any = await resolveWorkerReviewRow(row.id, body);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not update", type: "danger" });
        return;
      }
      if (body.action === "NOTE") {
        setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)));
        notifications({ title: "Saved", message: "Kept for later with your note", type: "success" });
        return;
      }
      removeRows([row.id]);
      reloadSetup();
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  const resolveSelected = async (action: "CREATE" | "SKIP" | "NOT_A_WORKER") => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    try {
      const res: any = await resolveWorkerReviewBulk({
        action,
        rowIds: ids,
        categoryId: action === "CREATE" ? bulkCategory || undefined : undefined,
      });
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not update", type: "danger" });
        return;
      }
      const failed: any[] = res.data?.failed || [];
      const failedIds = failed.map((f) => f.rowId);
      removeRows(ids.filter((id) => !failedIds.includes(id)));
      if (failed.length) {
        notifications({
          title: `${res.data.resolved} done, ${failed.length} not`,
          message: failed[0]?.reason || "Some rows need a category or name",
          type: "warning",
        });
      }
      reloadSetup();
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setBusy(false);
    }
  };

  if (!canManageKarkhana(role)) return null;

  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Workers" back={kp.workers()} />
      <WorkersNav reviewCount={rows.length} />

      <p className="kw-lede">
        These rows from an import need a person to decide. Nothing here has changed any worker already in HKP.
      </p>

      {counts.length > 1 ? (
        <div className="kw-filter-row" role="group" aria-label="Filter by reason">
          <button type="button" className={`kw-filter${filter === "" ? " is-active" : ""}`} onClick={() => setFilter("")}>
            All {rows.length}
          </button>
          {counts.map(([code, n]) => (
            <button
              key={code}
              type="button"
              className={`kw-filter${filter === code ? " is-active" : ""}`}
              onClick={() => setFilter(code)}
            >
              {issueInfo(code).label} {n}
            </button>
          ))}
        </div>
      ) : null}

      {visible.length > 0 && canWrite ? (
        <label className="kw-muted" style={{ display: "inline-flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={(e) =>
              setSelected(e.target.checked ? new Set(visible.map((r) => r.id)) : new Set())
            }
          />
          Select all shown
        </label>
      ) : null}

      {loading || !setupLoaded ? (
        <div className="kw-empty">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="kw-empty">
          {rows.length === 0 ? (
            <>
              Nothing to review. <Link to={kp.workerImport()}>Import a worker list</Link>
            </>
          ) : (
            "Nothing with this reason"
          )}
        </div>
      ) : (
        visible.map((row) => (
          <ReviewRow
            key={row.id}
            row={row}
            categories={categories}
            selected={selected.has(row.id)}
            leaving={leaving.has(row.id)}
            disabled={!canWrite || busy}
            onSelect={(checked) =>
              setSelected((prev) => {
                const next = new Set(prev);
                if (checked) next.add(row.id);
                else next.delete(row.id);
                return next;
              })
            }
            onResolve={resolveOne}
          />
        ))
      )}

      {selected.size > 0 ? (
        <div className="kw-bulkbar" role="region" aria-label="Bulk actions">
          <strong>{selected.size} selected</strong>
          <Input
            type="select"
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            style={{ width: "auto", height: 34 }}
            aria-label="Category for selected"
          >
            <option value="">Use suggested category</option>
            {categories
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.prefix} · {c.name}
                </option>
              ))}
          </Input>
          <Button size="sm" className="ResetBtn" disabled={busy} onClick={() => resolveSelected("CREATE")}>
            Add as new
          </Button>
          <Button size="sm" outline disabled={busy} onClick={() => resolveSelected("NOT_A_WORKER")}>
            Not workers
          </Button>
          <Button size="sm" outline disabled={busy} onClick={() => resolveSelected("SKIP")}>
            Skip
          </Button>
          <Button size="sm" outline disabled={busy} onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default KarkhanaWorkerReviewPage;
